import React, { useState } from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import {
  setDictionaryToAllDocs,
  setNewDictionary,
} from "slices/dictionarySearch/actions/categories";
import {
  BUTTON_MULTIPLE,
  BUTTON_SINGLE,
  DESCRIPTION_MULTIPLE,
  DESCRIPTION_SINGLE,
  folderBy,
  HEADER_MULTIPLE,
  HEADER_SINGLE,
} from "layout/dictionarySearch/constants";
import PropTypes from "prop-types";
import SelectDictTable from "components/SelectDictTable";
import { sortingKeys } from "constants/dictionary";

const ChangeDictionaryModal = ({ docId }) => {
  const dispatch = useDispatch();

  const dictionaries = useSelector(
    (state) => state.dictionarySearch.dictionaries
  );
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const activeDictionaryId = useSelector((state) =>
    by === folderBy.folder || (by === folderBy.document && docId === undefined)
      ? state.dictionarySearch.folder.dictionary.id
      : state.dictionarySearch.documents.find((d) => d.id === docId).dictionary
          .id
  );
  const filteredDictionaries = activeDictionaryId
    ? dictionaries.filter((dictionary) => dictionary.id !== activeDictionaryId)
    : dictionaries;

  const [selectedDictionaryId, setSelectedDictionaryId] = useState(null);
  const applyToMultiple = by === folderBy.document && docId === undefined;

  const headerConfig = [
    {
      title: `Dictionaries (${dictionaries.length - 1})`,
      sortBy: sortingKeys.name,
    },
    {
      title: "Created By",
      sortBy: sortingKeys.createdBy,
    },
    {
      title: "Created On",
      sortBy: sortingKeys.createdOn,
    },
    {
      title: "Last Updated",
      sortBy: sortingKeys.lastUpdated,
    },
    {
      title: "In Use",
      sortBy: sortingKeys.inUse,
    },
  ];

  const setDictionary = (dictionaryId) => {
    if (by === folderBy.folder) {
      dispatch(setNewDictionary(dictionaryId));
    } else if (applyToMultiple) {
      dispatch(setDictionaryToAllDocs(dictionaryId));
    } else {
      dispatch(setNewDictionary(dictionaryId, docId));
    }
  };

  const handleSelect = (dictionaryId) => {
    setSelectedDictionaryId(dictionaryId);
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
  };

  const handleChangeClick = () => {
    setDictionary(selectedDictionaryId);
  };

  const modalBody = () => {
    return (
      <div className="change_dictionary__body">
        <span className="change_dictionary__body__description">
          {applyToMultiple ? DESCRIPTION_MULTIPLE : DESCRIPTION_SINGLE}
        </span>
        {applyToMultiple && (
          <span className="change_dictionary__body__note">
            NOTE: If you have a lot of docs & a larger dictionary, this might
            take a minute or two. Please be patient.
          </span>
        )}
        <SelectDictTable
          dictionaries={filteredDictionaries}
          selectedDictionaryId={selectedDictionaryId}
          headerConfig={headerConfig}
          initColumnWidths={[218, 86, 88, 98, 45]}
          minimalColumnWidths={[40, 40, 40, 40, 40]}
          onDictionarySelect={handleSelect}
          onEnter={setDictionary}
        />
      </div>
    );
  };

  return (
    <Modal
      data-test="changeDictionaryModal"
      className="fade-in change_dictionary"
      show
      onDismiss={handleCancel}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <b>{applyToMultiple ? HEADER_MULTIPLE : HEADER_SINGLE}</b>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleChangeClick}
          variant={buttonVariants.primary}
          dataTestId="changeDictionaryButton"
          disabled={selectedDictionaryId === null}
          content={applyToMultiple ? BUTTON_MULTIPLE : BUTTON_SINGLE}
        />
        <Button
          onClick={handleCancel}
          variant={buttonVariants.secondary}
          data-test="cancelButton"
          disabled={false}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

ChangeDictionaryModal.propTypes = {
  docId: PropTypes.number,
};

export default ChangeDictionaryModal;
